// IBAN lengths per country code from the official SWIFT IBAN registry
export const IBAN_LENGTHS = {
  AD: 24, AE: 23, AL: 28, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22,
  BI: 27, BR: 29, BY: 28, CH: 21, CR: 22, CY: 28, CZ: 24, DE: 22, DJ: 27,
  DK: 18, DO: 28, EE: 20, EG: 29, ES: 24, FI: 18, FK: 18, FO: 18, FR: 27,
  GB: 22, GE: 22, GI: 23, GL: 18, GR: 27, GT: 28, HN: 28, HR: 21, HU: 28,
  IE: 22, IL: 23, IQ: 23, IS: 26, IT: 27, JO: 30, KW: 30, KZ: 20, LB: 28,
  LC: 32, LI: 21, LT: 20, LU: 20, LV: 21, LY: 25, MC: 27, MD: 24, ME: 22,
  MK: 19, MN: 20, MR: 27, MT: 31, MU: 30, NI: 28, NL: 18, NO: 15, OM: 23,
  PK: 24, PL: 28, PS: 29, PT: 25, QA: 29, RO: 24, RS: 22, RU: 33, SA: 24,
  SC: 31, SD: 18, SE: 24, SI: 19, SK: 24, SM: 27, SO: 23, ST: 25, SV: 28,
  TL: 23, TN: 24, TR: 26, UA: 29, VA: 22, VG: 24, XK: 20,
};

export const IBAN_MIN_LENGTH = 15;
export const IBAN_MAX_LENGTH = 34;

function normalize(value) {
  return (value || '').replace(/\s/g, '').toUpperCase();
}

// ISO 7064 mod-97-10 check over the rearranged IBAN, computed piecewise to
// stay within Number.MAX_SAFE_INTEGER
function passesMod97Check(iban) {
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const char of rearranged) {
    const value =
      char >= 'A' ? (char.charCodeAt(0) - 55).toString() : char;
    remainder = Number(String(remainder) + value) % 97;
  }
  return remainder === 1;
}

// Returns an untranslated error message (a key in the translation files) or
// null if the IBAN is valid. Empty values return null so `required()` can
// report them instead.
export function getIbanValidationError(value) {
  const iban = normalize(value);
  if (!iban) return null;

  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(iban))
    return 'Invalid IBAN format';

  const countryLength = IBAN_LENGTHS[iban.slice(0, 2)];
  if (countryLength) {
    if (iban.length !== countryLength)
      return 'Invalid IBAN length for this country';
  } else if (iban.length < IBAN_MIN_LENGTH || iban.length > IBAN_MAX_LENGTH) {
    return 'IBAN must be between 15 and 34 characters';
  }

  if (!passesMod97Check(iban)) return 'Invalid IBAN checksum';

  return null;
}

export function isValidIBAN(value) {
  return Boolean(normalize(value)) && getIbanValidationError(value) === null;
}

// BIC (ISO 9362): 4 letter bank code, 2 letter country code, 2 alphanumeric
// location code, optional 3 alphanumeric branch code
const BIC_REGEX = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

export function getBicValidationError(value) {
  const bic = normalize(value);
  if (!bic) return null;

  if (bic.length !== 8 && bic.length !== 11)
    return 'BIC must be 8 or 11 characters';
  if (!BIC_REGEX.test(bic)) return 'Invalid BIC format';

  return null;
}

export function isValidBIC(value) {
  return Boolean(normalize(value)) && getBicValidationError(value) === null;
}
